import type { QueryClient } from "@tanstack/react-query";

/** Anything on screen that means the page is still loading. */
const LOADING = ".animate-spin, .animate-pulse";
const MAX_WAIT_MS = 5000;

/**
 * Where to mount the app.
 *
 * Public pages are prerendered at deploy time (scripts/prerender.mjs), so
 * `#root` may already hold the page's static HTML. Rendering straight into it
 * would replace that content with loading spinners until the route chunk and
 * its data arrive. Instead the app mounts into a hidden sibling, and the two
 * are swapped once it has finished loading (or after a few seconds).
 */
export function mountTarget(root: HTMLElement, queryClient: QueryClient): HTMLElement {
  if (!("prerendered" in root.dataset)) return root;

  const live = document.createElement("div");
  live.hidden = true;
  root.after(live);

  const started = Date.now();
  const reveal = () => {
    const ready = live.querySelector("main") && !live.querySelector(LOADING) && queryClient.isFetching() === 0;
    if (!ready && Date.now() - started < MAX_WAIT_MS) {
      window.setTimeout(reveal, 50);
      return;
    }
    root.remove();
    live.id = "root";
    live.hidden = false;
  };
  window.setTimeout(reveal, 50);
  return live;
}
